// react
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// contexts
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { GeodataContext } from 'contexts/GeodataContext';
import { GridContext } from 'contexts/GridContext';
import { LastAddedItemContext } from 'contexts/LastAddedItemContext';
import { RunContext } from 'contexts/RunContext';
import { SimulationContext } from 'contexts/SimulationContext';
import { ValidationContext } from 'contexts/ValidationContext';
import { ValidationMinMaxContext } from 'contexts/ValidationMinMaxContext';
// gravity components
import { ViewContext, AuthContext } from 'components/lib';
// material ui
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// tree table
import { Body, Cell, Row, Table } from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { CellTree, TreeExpandClickTypes, useTree } from '@table-library/react-table-library/tree';
// services
import { doUppercaseFirstLetter } from 'utils/doUppercaseFirstLetter';
// my components
import PlayCircle from '@untitled-ui/icons-react/build/cjs/PlayCircle';
import {
  DeleteIcon,
  DescriptionIcon,
  DownDropDownArrow,
  getObjectIcon,
  InfoIcon,
  RightDropDownArrow,
  VerticalDotsIcon,
  CopyIcon,
  CheckedIcon,
  UnCheckedIcon,
  AlertIcon,
} from 'components/icons';

import {
  GridNode,
  GeodataNode,
  ProjectNode,
  SimulationNode,
  SubSimulation as SubSimulationType,
  ID,
} from 'types/project';
import { getProjectTableTheme } from './ProjectTableTheme';
import VisualizerLink from '../components/VisualizerLink';
import ProjectInfoTable from '../ProjectInfo';
import ProjectTableHeader from './ProjectTableHeader';
import EditableLabelState from '../components/EditableLabelState';
import { GeodataStatus } from './RenderStatus';
import SubSimulation from './SubSimulationBlock';
import Reports from '../Reports';
import ExportedFiles from '../ExportedFiles';
import ImportedFile from '../importedFile';
import { getProjectUsers, getProjectWithChildren } from '../../../api/projectApi';
import { cloneGrid, deleteGrid, getGridSize, updateGrid, type GridVolumeUsage } from '../../../api/gridApi';
import {
  cloneGeodata,
  deleteGeodata,
  downloadGeodataYaml,
  updateGeodata,
  type GeodataYamlKind,
} from '../../../api/geodataApi';
import {
  deleteSimulationSet,
  downloadSimulationSetYaml,
  getSimulationSet,
  updateSimulationSet,
} from '../../../api/simulationSetApi';
import { getSimulations } from '../../../api/simulationApi';
import { getCurrentUsage } from '../../../api/planApi';
import { isStorageLimitReached, STORAGE_LIMIT_REACHED_MESSAGE } from '../../../services/storageLimit';
import { normalizeProjectCoordinatesForMap } from '../../../utils/coordinateSystemManager';
import { openSupportCountsTowardUsageDialog, storePendingCountsTowardUsage } from '../../../services/countsTowardUsage';
import { getRunnableSimulationCount, getSimulationLimitMessage } from '../../../services/simulationUsage';
import { UMButton, UMLoader } from '@urbanmetrix/um-kit';
import { GRID_NAME_MAX_LENGTH } from 'constants/grid';
import { formatBytes } from '../../../utils/formatBytes';
import VolumeUsageDialog from '../../../components/volume-usage-dialog/VolumeUsageDialog';
import { requiresVolumePayment } from '../../../services/gridLimitChecks';
import { getApiErrorMessage } from '../../../api/apiClient';
import {
  EMPTY_ENTITY_SIZE_MAP,
  GEODATA_YAML_OPTIONS,
  CLOSED_NODES_STORAGE_PREFIX,
  SUPPORT_FLAG_REQUIRED_MESSAGE,
  getDraftResumeTarget,
  filterDeletedProjectItems,
  getStoredClosedNodes,
  getTreeNodeIds,
  mapGridSizeResultsToEntitySizes,
  normalizeDateString,
  toFiniteNumberOrNull,
  type EntitySizeMap,
  type RunStateMap,
  type TreeData,
  type TreeNode,
} from './projectTableUtils';

const RowComponent = Row as unknown as React.FC<any>;
const CellTreeComponent = CellTree as unknown as React.FC<any>;

const RemainingCountAlertIcon = ({
  pluralLabel,
  size,
  className,
}: {
  pluralLabel: string;
  size?: number | string;
  className?: string;
}) => (
  <Tooltip title={`No ${pluralLabel} are available on this project.`}>
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <AlertIcon className={className} size={size} color="#dc2626" />
    </span>
  </Tooltip>
);

const Project = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const theme = useTheme(getProjectTableTheme());
  const navigate = useNavigate();

  // Contexts
  const { lastAddedItem } = useContext(LastAddedItemContext);
  const { clearGridFormData } = useContext(GridContext);
  const { clearGeodataFormData } = useContext(GeodataContext);
  const { clearSimulationFormData, cloneSimulationData, setIsCloningSimulation } = useContext(SimulationContext);
  const context = useContext(ViewContext);
  const { clearValidationForSimulationWindSimulations } = useContext(ValidationContext);
  const { clearValidationGridMinMaxInputs } = useContext(ValidationMinMaxContext);
  const runContext = useContext(RunContext);
  const {
    gridIdOpenButtonMap,
    geodataStatuses,
    updateGeodataActive,
    removeGeodata,
    allSubSimulations,
    setAllSimulations,
    geodataTaskStatuses,
    handleRunSimulation,
    allGeodatas,
    isRunDataLoading,
    removeSimulation,
  } = runContext;
  const authContext = useContext(AuthContext);
  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;
  const currentUserId = authContext.user?.id;
  const showDeletedItems =
    ['dev', 'support'].includes(authContext.user?.appRole ?? '') &&
    Boolean(currentUserId && localStorage.getItem(`dashboard.showDeletedProjects.${currentUserId}`) === 'true');
  const showSizes = Boolean(currentUserId && localStorage.getItem(`dashboard.showSizes.${currentUserId}`) === 'true');
  const [, setIsLoadingProject] = useState(false);
  const [isStorageLimitReachedForRuns, setIsStorageLimitReachedForRuns] = useState(false);
  const [isGridCloneInProgress, setIsGridCloneInProgress] = useState(false);
  const [isGeodataCloneInProgress, setIsGeodataCloneInProgress] = useState(false);
  const [isSimulationCloneInProgress, setIsSimulationCloneInProgress] = useState(false);
  const isMountedRef = useRef(true);
  const filesSectionRef = useRef<HTMLDivElement>(null);
  const filesHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isFilesSectionHighlighted, setIsFilesSectionHighlighted] = useState(false);

  const [runStates, setRunStates] = useState<RunStateMap>({});
  const [volumeDialogGridId, setVolumeDialogGridId] = useState<string | null>(null);
  const [volumeDialogRunItem, setVolumeDialogRunItem] = useState<SimulationNode | null>(null);
  const [entitySizes, setEntitySizes] = useState<EntitySizeMap>(EMPTY_ENTITY_SIZE_MAP);
  const previousGeodataStatusesRef = useRef<Record<string, string>>({});
  const previousSubSimulationStatusesRef = useRef<Record<string, string>>({});
  const isRefreshingProjectAfterSuccessRef = useRef(false);
  const isProjectRefreshQueuedRef = useRef(false);

  const scrollToFiles = useCallback(() => {
    filesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setIsFilesSectionHighlighted(true);
    if (filesHighlightTimerRef.current) clearTimeout(filesHighlightTimerRef.current);
    filesHighlightTimerRef.current = setTimeout(() => setIsFilesSectionHighlighted(false), 1200);
  }, []);

  useEffect(
    () => () => {
      if (filesHighlightTimerRef.current) clearTimeout(filesHighlightTimerRef.current);
    },
    []
  );

  // Clear all data
  useEffect(() => {
    clearGridFormData();
    clearGeodataFormData();
    clearSimulationFormData();
    clearValidationGridMinMaxInputs();
    clearValidationForSimulationWindSimulations();
  }, []);

  // Actions Detail
  const [selectedItem, setSelectedItem] = useState<TreeNode | null>(null);
  const [anchorElActionsDetail, setAnchorElActionsDetail] = useState<HTMLElement | null>(null);
  const [anchorElGeodataYaml, setAnchorElGeodataYaml] = useState<HTMLElement | null>(null);
  const openActionsDetail = Boolean(anchorElActionsDetail);
  const openGeodataYamlMenu = Boolean(anchorElGeodataYaml);

  const handleClickActionsDetail = (event: React.MouseEvent<HTMLElement>, item: TreeNode) => {
    setAnchorElGeodataYaml(null);
    setAnchorElActionsDetail(event.currentTarget);
    setSelectedItem(item);
  };

  const handleCloseActionsDetail = () => {
    setAnchorElActionsDetail(null);
  };

  const handleMenuCloseActionsDetail = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
    handleCloseActionsDetail();
  };

  const handleOpenGeodataYamlMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorElGeodataYaml(anchorElActionsDetail ?? event.currentTarget);
    handleCloseActionsDetail();
  };

  const handleCloseGeodataYamlMenu = () => {
    setAnchorElGeodataYaml(null);
  };

  const { currentProject, setCurrentProject } = useContext(CurrentProjectContext);

  const isStaleProject = currentProject && projectId ? String(currentProject.id) !== String(projectId) : false;
  const canCreateInCurrentProject =
    Boolean(authContext.permission?.editor) ||
    (Boolean(authContext.permission?.support) && Boolean(currentProject?.supportFlagged));
  const isResourceDeleteDisabled = !canCreateInCurrentProject;

  const loadProjectPageData = useCallback(
    async (resolvedProjectId: string, shouldAbort: () => boolean = () => false) => {
      if (isMountedRef.current) {
        setIsLoadingProject(true);
      }

      const [rawProject, projectUsers, usage] = await Promise.all([
        getProjectWithChildren(resolvedProjectId),
        getProjectUsers(resolvedProjectId),
        getCurrentUsage(),
      ]);

      if (!isMountedRef.current || shouldAbort()) return;

      if (!rawProject?.id) {
        navigate('/dashboard');
        return;
      }

      const filteredProjectGrids = Array.isArray(rawProject?.grids) ? rawProject.grids : [];
      const projectOrganizationId = rawProject.organizationId ?? activeOrganizationId ?? null;
      const owner = projectUsers?.find(
        (user: { isProjectOwner?: boolean; name?: string; id?: string | number }) => user.isProjectOwner
      );

      const normalizedProject: ProjectNode = normalizeProjectCoordinatesForMap({
        ...rawProject,
        address: rawProject.address ?? undefined,
        coordinates: rawProject.projectCoordinates ?? undefined,
        createdAt: normalizeDateString(rawProject.createdAt),
        geodatasAvailableCount: toFiniteNumberOrNull(
          (rawProject as { geodatasAvailableCount?: unknown }).geodatasAvailableCount
        ),
        simulationsAvailableCount: toFiniteNumberOrNull(
          (rawProject as { simulationsAvailableCount?: unknown }).simulationsAvailableCount
        ),
        owner: owner ? { userId: owner.id, name: owner.name } : null,
        grids:
          filteredProjectGrids?.map((grid: any) => ({
            ...grid,
            organizationId: grid.organizationId ?? projectOrganizationId,
            createdAt: normalizeDateString(grid.createdAt),
            type: 'grid',
            geodatas:
              (grid?.geodatas ?? []).map((geodata: any) => ({
                ...geodata,
                gridId: geodata.gridId ?? grid.id,
                projectId: geodata.projectId ?? rawProject.id,
                organizationId:
                  geodata.organizationId ?? rawProject.organizationId ?? grid.organizationId ?? projectOrganizationId,
                createdAt: normalizeDateString(geodata.createdAt),
                lifecycleStatus: geodata.status,
                progressStatus: geodata.progressStatus,
                status: geodata.progressStatus,
                statusMessage: geodata.errorMessage,
                failedOnStep: geodata.failedOnStep,
                execTime: geodata.execTime,
                type: 'geodata',
                simulations: (geodata?.simulationSets ?? []).map((simSet: any) => ({
                  id: simSet.id,
                  projectId: simSet.projectId ?? rawProject.id,
                  gridId: simSet.gridId ?? grid.id,
                  geodataId: geodata.id,
                  organizationId:
                    simSet.organizationId ??
                    geodata.organizationId ??
                    rawProject.organizationId ??
                    projectOrganizationId,
                  runId: null,
                  name: simSet.name,
                  description: simSet.description,
                  deletedAt: normalizeDateString(simSet.deletedAt),
                  analysisType: geodata.analysisType,
                  type: 'simulation',
                  subSimulations: (simSet.simulations ?? []).map((sub: any) => ({
                    ...sub,
                    simType: String(sub.simType ?? '').toUpperCase(),
                    type: sub.type ?? 'subsimulation',
                    simulationSetId: simSet.id,
                    organizationId:
                      sub.organizationId ??
                      simSet.organizationId ??
                      geodata.organizationId ??
                      rawProject.organizationId ??
                      projectOrganizationId,
                    execTime: sub.execTime,
                    statusMessage: sub.errorMessage,
                    failedOnStep: sub.failedOnStep,
                    isActive: sub?.isActive === 1 || sub?.isActive === true,
                  })),
                })),
              })) ?? [],
          })) ?? [],
      }) as ProjectNode;

      setCurrentProject(normalizedProject);
      setIsStorageLimitReachedForRuns(isStorageLimitReached(usage));
      document.title = 'Project: ' + (normalizedProject.name ?? '');
    },
    [activeOrganizationId, navigate, setCurrentProject]
  );

  const refreshProjectAfterSuccess = useCallback(async () => {
    if (!projectId) {
      return;
    }

    if (isRefreshingProjectAfterSuccessRef.current) {
      isProjectRefreshQueuedRef.current = true;
      return;
    }

    isRefreshingProjectAfterSuccessRef.current = true;

    try {
      do {
        isProjectRefreshQueuedRef.current = false;
        await loadProjectPageData(projectId);
      } while (isProjectRefreshQueuedRef.current);
    } catch (error) {
      context.notification.show(
        getApiErrorMessage(error, 'Unable to refresh project data right now.'),
        'error',
        true,
        'toast'
      );
    } finally {
      isRefreshingProjectAfterSuccessRef.current = false;
    }
  }, [context.notification, loadProjectPageData, projectId]);

  const runSimulation = (item: SimulationNode) => {
    setAllSimulations((prevStatuses: Record<ID, SubSimulationType[]>) => {
      const currentStatusArray = item.subSimulations.map((subSim) => ({
        ...subSim,
        status: 'INITIALIZING',
      }));
      return {
        ...prevStatuses,
        [item.id]: currentStatusArray,
      };
    });

    setRunStates((prevStates) => ({
      ...prevStates,
      [item.id]: true,
    }));

    handleRunSimulation(item, 'java');
  };

  const handleRunClick = (item: SimulationNode) => {
    if (isStorageLimitReachedForRuns) {
      context.notification.show(STORAGE_LIMIT_REACHED_MESSAGE, 'error', true, 'toast');
      return;
    }

    const grid = currentProject?.grids.find((candidate) => String(candidate.id) === String(item.gridId));
    if (grid && requiresVolumePayment(grid.gridVolume, currentProject?.paidVolume)) {
      setVolumeDialogGridId(String(grid.id));
      setVolumeDialogRunItem(item);
      return;
    }

    runSimulation(item);
  };

  useEffect(() => {
    let shouldRefreshProject = false;

    Object.entries(geodataStatuses).forEach(([geodataId, status]) => {
      const previousStatus = previousGeodataStatusesRef.current[geodataId];
      if (status === 'SUCCEEDED' && previousStatus !== 'SUCCEEDED') {
        shouldRefreshProject = true;
      }
    });

    const nextGeodataStatuses: Record<string, string> = {};
    Object.entries(geodataStatuses).forEach(([geodataId, status]) => {
      nextGeodataStatuses[geodataId] = status;
    });
    previousGeodataStatusesRef.current = nextGeodataStatuses;

    Object.entries(allSubSimulations).forEach(([simulationId, subSimulations]) => {
      subSimulations.forEach((subSim) => {
        const subSimulationKey = `${simulationId}:${String(subSim.id)}`;
        const previousStatus = previousSubSimulationStatusesRef.current[subSimulationKey];
        if (subSim.status === 'SUCCEEDED' && previousStatus !== 'SUCCEEDED') {
          shouldRefreshProject = true;
        }
      });
    });

    const nextSubSimulationStatuses: Record<string, string> = {};
    Object.entries(allSubSimulations).forEach(([simulationId, subSimulations]) => {
      subSimulations.forEach((subSim) => {
        nextSubSimulationStatuses[`${simulationId}:${String(subSim.id)}`] = subSim.status ?? '';
      });
    });
    previousSubSimulationStatusesRef.current = nextSubSimulationStatuses;

    if (shouldRefreshProject) {
      void refreshProjectAfterSuccess();
    }
  }, [allSubSimulations, geodataStatuses, refreshProjectAfterSuccess]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset project data on route change to avoid rendering stale tree before live data loads.
  useEffect(() => {
    setCurrentProject(null);
  }, [projectId, setCurrentProject]);

  useEffect(() => {
    let cancelled = false;

    async function loadProject() {
      if (!projectId) {
        return;
      }
      try {
        await loadProjectPageData(projectId, () => cancelled);
      } catch (error) {
        console.error('Error fetching project page data:', error);
      } finally {
        if (isMountedRef.current && !cancelled) {
          setIsLoadingProject(false);
        }
      }
    }

    loadProject();

    return () => {
      cancelled = true;
    };
  }, [loadProjectPageData, projectId]);

  /**
   * Converting the project data structure to nodes structure
   */
  const convertSimulationToNode = (simulation: SimulationNode, analysisType?: string): SimulationNode => {
    const { subSimulations, ...otherProps } = simulation;
    return {
      ...otherProps,
      analysisType,
      subSimulations,
      nodes: [], // keep tree aware for expand/collapse without rendering children as rows
    };
  };

  const convertGeodataToNode = (geodata: GeodataNode, analysisType?: string): GeodataNode => {
    const { simulations, ...otherProps } = geodata;
    return {
      ...otherProps,
      analysisType,
      nodes: simulations.map((simulation) => convertSimulationToNode(simulation, analysisType)),
      simulations,
    };
  };

  const convertGridToNode = (grid: GridNode, analysisType?: string): GridNode => {
    const { geodatas, ...otherProps } = grid;
    return {
      ...otherProps,
      analysisType,
      nodes: geodatas.map((geodata) => convertGeodataToNode(geodata, analysisType)),
      geodatas,
    };
  };

  const convertProjectToNode = (project: ProjectNode): GridNode[] => {
    const { grids, analysisType } = project;
    const normalizedGrids = Array.isArray(grids) ? grids : [];
    return normalizedGrids.map((grid) => convertGridToNode(grid, analysisType));
  };

  const data = useMemo<TreeData>(() => {
    if (!currentProject) return { nodes: [] };

    const updatedGeodata: ProjectNode = {
      ...currentProject,
      grids: (currentProject.grids ?? []).map((grid) => ({
        ...grid,
        geodatas: (grid.geodatas ?? []).map((geodata) => {
          const liveGeodata = allGeodatas[geodata.id];
          const liveStatus = geodataStatuses[geodata.id];
          return {
            ...geodata,
            ...(liveGeodata ?? {}),
            status: liveStatus ?? liveGeodata?.status ?? geodata.status,
            isActive: liveGeodata?.isActive ?? geodata.isActive,
          };
        }),
      })),
    };

    return { nodes: convertProjectToNode(filterDeletedProjectItems(updatedGeodata, showDeletedItems)) };
  }, [allGeodatas, currentProject, geodataStatuses, showDeletedItems]);

  const allNodeIds = useMemo(() => getTreeNodeIds(data.nodes), [data.nodes]);
  const [closedNodesState, setClosedNodesState] = useState(() => ({
    projectId,
    ids: getStoredClosedNodes(projectId),
  }));
  const closedNodes = closedNodesState.projectId === projectId ? closedNodesState.ids : getStoredClosedNodes(projectId);
  const openNodes = allNodeIds.filter((id) => !closedNodes.includes(id));
  const setOpenNodes = (nextValue: React.SetStateAction<ID[]>) => {
    const nextOpenIds = typeof nextValue === 'function' ? nextValue(openNodes) : nextValue;
    const nextClosedIds = allNodeIds.filter((id) => !nextOpenIds.includes(id));
    setClosedNodesState({ projectId, ids: nextClosedIds });
    if (projectId) {
      try {
        localStorage.setItem(`${CLOSED_NODES_STORAGE_PREFIX}${projectId}`, JSON.stringify(nextClosedIds));
      } catch {
        // Keep manual expansion working when storage is unavailable.
      }
    }
  };

  const gridIds = useMemo(
    () => (currentProject?.grids ?? []).map((grid) => String(grid.id)).filter(Boolean),
    [currentProject?.grids]
  );
  const gridIdsKey = useMemo(() => gridIds.join(','), [gridIds]);

  useEffect(() => {
    let cancelled = false;

    async function loadEntitySizes() {
      if (!showSizes) {
        setEntitySizes(EMPTY_ENTITY_SIZE_MAP);
        return;
      }
      if (gridIds.length === 0) {
        setEntitySizes(EMPTY_ENTITY_SIZE_MAP);
        return;
      }

      const sizeResponses = await Promise.allSettled(gridIds.map((gridId) => getGridSize(gridId)));
      if (cancelled) return;

      const fulfilledResponses = sizeResponses.flatMap((result) =>
        result.status === 'fulfilled' ? [result.value] : []
      );
      const rejectedResponses = sizeResponses.filter((result) => result.status === 'rejected');

      if (rejectedResponses.length > 0) {
        console.error('Unable to load one or more grid size payloads:', rejectedResponses);
      }

      setEntitySizes(mapGridSizeResultsToEntitySizes(fulfilledResponses));
    }

    loadEntitySizes().catch((error) => {
      if (cancelled) return;
      console.error('Error loading grid size payloads:', error);
      setEntitySizes(EMPTY_ENTITY_SIZE_MAP);
    });

    return () => {
      cancelled = true;
    };
  }, [currentProject?.id, gridIdsKey, showSizes]);

  /**
   * Go to the relevant input page
   */
  const handleClickAdd = (item: TreeNode, hasNoRemainingCountOverride?: boolean) => {
    if (!canCreateInCurrentProject) {
      return;
    }

    const navigateToCreationFlow = (path: string) => {
      const navigateWithParentOpen = () => {
        setOpenNodes((currentOpenNodes) =>
          currentOpenNodes.includes(item.id) ? currentOpenNodes : [...currentOpenNodes, item.id]
        );
        navigate(path);
      };

      if (authContext.permission?.support) {
        const isGeodataCreation = item.type === 'grid';
        const remainingCount = isGeodataCreation
          ? currentProject?.geodatasAvailableCount
          : currentProject?.simulationsAvailableCount;
        const hasNoRemainingCount =
          typeof hasNoRemainingCountOverride === 'boolean'
            ? hasNoRemainingCountOverride
            : typeof remainingCount === 'number' && remainingCount <= 0;

        openSupportCountsTowardUsageDialog({
          context,
          entityLabel: isGeodataCreation ? 'geodata' : 'simulation set',
          defaultCountsTowardUsage: !hasNoRemainingCount,
          disableCountTowardUsage: hasNoRemainingCount,
          onContinue: (countsTowardUsage: boolean) => {
            storePendingCountsTowardUsage(countsTowardUsage);
            navigateWithParentOpen();
          },
        });
        return;
      }

      navigateWithParentOpen();
    };

    switch (item.type) {
      case 'grid':
        navigateToCreationFlow(`/geodatas/add/${item.id}`);
        break;
      case 'geodata':
        setIsCloningSimulation(false);
        navigateToCreationFlow(`/simulations/add/${item.id}`);
        break;
      default:
        break;
    }
  };

  const handleResumeDraftEdit = useCallback(
    (item: TreeNode | null) => {
      const target = getDraftResumeTarget(item);
      if (!target) {
        return;
      }

      handleCloseActionsDetail();

      const nextParams = new URLSearchParams({
        step: target.step,
        projectId: target.projectId,
        gridId: target.gridId,
        geodataId: target.geodataId,
      });
      if (target.simulationSetId) {
        nextParams.set('simulationSetId', target.simulationSetId);
      }

      navigate(`/projects/add?${nextParams.toString()}`);
    },
    [navigate]
  );

  /**
   * Delete item from tree table
   */
  const handleDeleteItem = (pItem: TreeNode | null) => {
    if (!pItem || !currentProject) return;
    context.modal.show(
      {
        title: `Delete ${doUppercaseFirstLetter(pItem.type)}`,
        form: {},
        buttonText: 'Delete',
        text: `Are you sure you want to delete ${pItem.name}?`,
        ...(pItem.type === 'grid'
          ? {
              customSubmit: () => deleteGrid(String(pItem.id)),
            }
          : pItem.type === 'geodata'
            ? {
                customSubmit: () => deleteGeodata(String(pItem.id)),
              }
            : pItem.type === 'simulation'
              ? {
                  customSubmit: () => deleteSimulationSet(String(pItem.id)),
                }
              : {}),
        destructive: true,
        toastSuccess: false,
      },
      () => {
        const updatedCurrentProject: ProjectNode = { ...currentProject };
        switch (pItem.type) {
          case 'grid':
            updatedCurrentProject.grids = updatedCurrentProject.grids.filter((grid) => grid.id !== pItem.id);
            break;
          case 'geodata':
            updatedCurrentProject.grids = updatedCurrentProject.grids.map((grid) => ({
              ...grid,
              geodatas: grid.geodatas.filter((geodata) => geodata.id !== pItem.id),
            }));

            removeGeodata(pItem.id);

            updatedCurrentProject.grids.forEach(async (grid) => {
              if (grid.geodatas.length === 0) {
                grid.openVisualizerBtn = false;
              }
            });
            break;
          case 'simulation':
            const newSimulations = { ...allSubSimulations };
            delete newSimulations[pItem.id]; // Assuming pItem.id is the key in the allSimulations object
            setAllSimulations(newSimulations);
            removeSimulation(pItem.id);
            updatedCurrentProject.grids = updatedCurrentProject.grids.map((grid) => ({
              ...grid,
              geodatas: grid.geodatas.map((geodata) => ({
                ...geodata,
                simulations: geodata.simulations.filter((simulation) => simulation.id !== pItem.id),
              })),
            }));

            break;
          default:
            break;
        }
        setCurrentProject(updatedCurrentProject);
        context.notification.show(pItem.name + ' was deleted', 'success', true, 'toast');
      }
    );
    handleCloseActionsDetail();
  };

  const tree = useTree(
    data,
    {
      state: { ids: openNodes },
      onChange: (action, state) => {
        if (action.type !== 'SET') setOpenNodes(state.ids ?? []);
      },
    },
    {
      treeIcon: {
        iconRight: (item: TreeNode) => {
          return (item.nodes && item.nodes.length > 0) || (item.subSimulations && item.subSimulations.length > 0) ? (
            <RightDropDownArrow />
          ) : (
            <RightDropDownArrow visibility={'hidden'} />
          );
        },
        iconDown: (item: TreeNode) => {
          return (item.nodes && item.nodes.length > 0) || (item.subSimulations && item.subSimulations.length > 0) ? (
            <DownDropDownArrow />
          ) : (
            <DownDropDownArrow visibility={'hidden'} />
          );
        },
      },
      indentation: 30,
      clickType: TreeExpandClickTypes?.ButtonClick,
    }
  );

  const selectedGrid =
    selectedItem?.type === 'grid' ? data.nodes.find((grid) => grid.id === selectedItem.id) : undefined;
  const isGridCloneDisabled = isGridCloneInProgress || !selectedGrid || selectedGrid.status === 'DRAFT';
  const gridCloneTooltip = isGridCloneInProgress
    ? 'Cloning grid...'
    : isGridCloneDisabled
      ? 'Cloning grid is only available after the grid has been completed'
      : `Clone ${selectedGrid?.name ?? 'Grid'}`;
  const selectedGeodataGrid =
    selectedItem?.type === 'geodata'
      ? data.nodes.find((grid) => String(grid.id) === String(selectedItem.gridId))
      : undefined;
  const isGeodataCloneDisabled =
    isGeodataCloneInProgress || !selectedGeodataGrid || selectedGeodataGrid.status === 'DRAFT';
  const geodataCloneTooltip = isGeodataCloneInProgress
    ? 'Cloning geodata...'
    : isGeodataCloneDisabled
      ? 'Cloning geodata is only available after the grid has been completed'
      : `Clone ${selectedItem?.name ?? 'Geodata'}`;
  const selectedSimulationGrid =
    selectedItem?.type === 'simulation'
      ? data.nodes.find(
          (grid) =>
            String(grid.id) === String(selectedItem.gridId) ||
            grid.geodatas?.some((geodata) =>
              geodata.simulations?.some((simulation) => String(simulation.id) === String(selectedItem.id))
            )
        )
      : undefined;
  const isSimulationCloneDisabled =
    isSimulationCloneInProgress || !selectedSimulationGrid || selectedSimulationGrid.status === 'DRAFT';
  const simulationCloneTooltip = isSimulationCloneInProgress
    ? 'Cloning simulation...'
    : isSimulationCloneDisabled
      ? 'Cloning simulation is only available after the grid has been completed'
      : `Clone ${selectedItem?.name ?? 'Simulation'}`;

  const handleClone = async (pItem: TreeNode | null) => {
    if (!pItem || pItem.type !== 'grid' || isGridCloneDisabled) return;

    setIsGridCloneInProgress(true);
    try {
      const clonedGrid = await cloneGrid(String(pItem.id));
      const nextParams = new URLSearchParams({
        step: 'grid',
        gridStep: 'analysis-volume',
        projectId: String(clonedGrid.projectId ?? pItem.projectId),
        gridId: String(clonedGrid.id),
      });
      if (clonedGrid.geodataId) {
        nextParams.set('geodataId', clonedGrid.geodataId);
      }
      if (clonedGrid.simulationSetId) {
        nextParams.set('simulationSetId', clonedGrid.simulationSetId);
      }
      context.notification.show(`${pItem.name} was cloned`, 'success', true, 'toast');
      handleCloseActionsDetail();
      navigate(`/projects/add?${nextParams.toString()}`);
    } catch (error) {
      console.error('Failed to clone grid:', error);
      context.notification.show('Failed to clone grid', 'error', true, 'toast');
    } finally {
      setIsGridCloneInProgress(false);
    }
  };

  const handleGeodataClone = async (pItem: TreeNode | null) => {
    if (!pItem || pItem.type !== 'geodata' || isGeodataCloneDisabled) return;

    setIsGeodataCloneInProgress(true);
    try {
      const clonedGeodata = await cloneGeodata(String(pItem.id));
      context.notification.show(`${pItem.name} was cloned`, 'success', true, 'toast');
      handleCloseActionsDetail();
      navigate(`/geodatas/add/${pItem.gridId}?geodataId=${clonedGeodata.id}`);
    } catch (error) {
      console.error('Failed to clone geodata:', error);
      context.notification.show(getApiErrorMessage(error, 'Failed to clone geodata'), 'error', true, 'toast');
    } finally {
      setIsGeodataCloneInProgress(false);
    }
  };

  const handleSimulationClone = (pItem: TreeNode | null) => {
    if (!pItem || pItem.type !== 'simulation' || isSimulationCloneDisabled) return;

    const startClone = async () => {
      setIsSimulationCloneInProgress(true);
      try {
        const [simulationSet, subSimulations] = await Promise.all([
          getSimulationSet(String(pItem.id)),
          getSimulations(String(pItem.id)),
        ]);
        cloneSimulationData({ ...pItem, ...simulationSet, subSimulations });
        setIsCloningSimulation(true);
        clearValidationForSimulationWindSimulations();
        handleCloseActionsDetail();
        navigate(`/simulations/add/${pItem.geodataId}`);
      } catch (error) {
        console.error('Failed to load simulation values for cloning:', error);
        context.notification.show(getApiErrorMessage(error, 'Failed to clone simulation'), 'error', true, 'toast');
      } finally {
        setIsSimulationCloneInProgress(false);
      }
    };

    if (authContext.permission?.support) {
      const remainingCount = currentProject?.simulationsAvailableCount;
      const hasNoRemainingCount = typeof remainingCount === 'number' && remainingCount <= 0;
      openSupportCountsTowardUsageDialog({
        context,
        entityLabel: 'simulation set',
        defaultCountsTowardUsage: !hasNoRemainingCount,
        disableCountTowardUsage: hasNoRemainingCount,
        onContinue: (countsTowardUsage: boolean) => {
          storePendingCountsTowardUsage(countsTowardUsage);
          void startClone();
        },
      });
      return;
    }

    void startClone();
  };

  const handleDownloadYaml = async (pItem: TreeNode | null, yamlKind?: GeodataYamlKind) => {
    if (!pItem || (pItem.type !== 'geodata' && pItem.type !== 'simulation')) {
      return;
    }

    if (pItem.type === 'geodata' && !yamlKind) {
      return;
    }

    try {
      const downloadUrl =
        pItem.type === 'geodata'
          ? downloadGeodataYaml(String(pItem.id), yamlKind as GeodataYamlKind)
          : downloadSimulationSetYaml(String(pItem.id));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      link.remove();

      handleCloseGeodataYamlMenu();
      handleCloseActionsDetail();
    } catch (error) {
      console.error('Error downloading yaml:', error);
      context.notification.show(
        `Unable to download ${pItem.type === 'geodata' ? `${yamlKind ?? 'geodata'} yaml` : 'simulation set yaml'}`,
        'error',
        true,
        'toast'
      );
    }
  };

  const createVisualizerLink = (item: GridNode) => {
    const geodatasForGrid = item.geodatas ?? [];
    const hasAnyGeodatas = geodatasForGrid.length > 0;
    const succeededGeodatas = geodatasForGrid.filter((g) => (geodataStatuses[g.id] ?? g.status) === 'SUCCEEDED');
    const hasSucceededGeodatas = succeededGeodatas.length > 0;
    const activeGeodatas = succeededGeodatas.some((g) => g.isActive);

    let disabled = false;
    let disabledReason = '';

    // Fallback: if backend map is missing, allow enabling when a gathered geodata exists.
    const canOpen = (gridIdOpenButtonMap[item.id] ?? hasSucceededGeodatas) && hasSucceededGeodatas;

    if (!hasAnyGeodatas) {
      disabled = true;
      disabledReason = 'Create a Geodata';
    } else if (!hasSucceededGeodatas || !canOpen) {
      disabled = true;
      disabledReason = 'Gather a Geodata';
    } else if (!activeGeodatas) {
      disabled = true;
      disabledReason = 'Please flag at least one geodata';
    }

    return (
      <VisualizerLink disabled={disabled} disabledReason={disabledReason} gridId={item.id}>
        <div className="open-visualizer-btn">
          <span>3D</span>
        </div>
      </VisualizerLink>
    );
  };

  return (
    <div style={{ overflow: 'auto', height: '95%', padding: '20px 30px' }}>
      <div id="project-page">
        {currentProject && (
          <ProjectInfoTable
            currentProject={currentProject}
            onRefreshProject={() => void refreshProjectAfterSuccess()}
            onScrollToFiles={scrollToFiles}
          />
        )}

        {!currentProject || isRunDataLoading || isStaleProject ? (
          <div className="review-loader-center">
            <UMLoader />
          </div>
        ) : data.nodes.length === 0 ? (
          <Alert sx={{ alignItems: 'center' }} severity="info" className="stat">
            No grid found
          </Alert>
        ) : (
          <Table data={data} tree={tree} theme={theme} layout={{ custom: true }} className="tree-table">
            {(tableList: TreeNode[], index: number) => (
              <React.Fragment key={`table_fragment_${index}`}>
                <ProjectTableHeader showSizes={showSizes} />

                <Body>
                  {tableList.map((item: TreeNode) => {
                    const isOpen = openNodes.includes(item.id); // Check if the current node is open
                    const isExpandableRow =
                      item.type === 'grid' || item.type === 'geodata' || item.type === 'simulation';
                    const geodataStatus =
                      item.type === 'geodata' ? (geodataStatuses[item.id] ?? item.status ?? '') : undefined;
                    const isGeodataSucceeded = geodataStatus === 'SUCCEEDED';
                    const isGeodataFailed =
                      geodataStatus === 'FAILED' || geodataStatus === 'TIMED_OUT' || geodataStatus === 'ABORTED';
                    const canAddSimulation = item.type !== 'geodata' || !isGeodataFailed;

                    const remainingGeodatas = toFiniteNumberOrNull(currentProject?.geodatasAvailableCount);
                    const remainingSimulations = toFiniteNumberOrNull(currentProject?.simulationsAvailableCount);
                    const hasNoGeodatasAvailable = remainingGeodatas !== null && remainingGeodatas <= 0;
                    const hasNoSimulationsAvailable = remainingSimulations !== null && remainingSimulations <= 0;
                    const canBypassUsageLimits = Boolean(authContext.permission?.support);
                    const isGridDraft = item.type === 'grid' && item.status === 'DRAFT';
                    const geodataButtonDisabled =
                      item.type === 'grid' &&
                      (isGridDraft || !canCreateInCurrentProject || (hasNoGeodatasAvailable && !canBypassUsageLimits));
                    const parentGrid =
                      item.type === 'geodata'
                        ? currentProject.grids?.find((grid) => String(grid.id) === String(item.gridId))
                        : null;
                    const isParentGridDraft = parentGrid?.status === 'DRAFT';
                    const simulationButtonDisabled =
                      item.type === 'geodata' &&
                      (!canAddSimulation ||
                        isParentGridDraft ||
                        !canCreateInCurrentProject ||
                        (hasNoSimulationsAvailable && !canBypassUsageLimits));
                    const geodataButtonTooltip = isGridDraft
                      ? ''
                      : !canCreateInCurrentProject
                        ? SUPPORT_FLAG_REQUIRED_MESSAGE
                        : hasNoGeodatasAvailable
                          ? 'No geodata modules are available on this project.'
                          : '';
                    const simulationButtonTooltip = !canCreateInCurrentProject
                      ? SUPPORT_FLAG_REQUIRED_MESSAGE
                      : hasNoSimulationsAvailable
                        ? 'No simulations are available on this project.'
                        : '';
                    const simGeodataStatus =
                      item.type === 'simulation'
                        ? (geodataStatuses[item.geodataId] ?? allGeodatas[item.geodataId]?.status ?? '')
                        : undefined;

                    const terminalStatuses = ['SUCCEEDED', 'FAILED', 'ABORTED', 'TIMED_OUT'];
                    const simSubSimulations =
                      item.type === 'simulation'
                        ? (allSubSimulations[item.id] ?? item.subSimulations ?? []).filter(
                            (subSimulation) => showDeletedItems || !subSimulation.deletedAt
                          )
                        : [];
                    const requiredSimulationRuns =
                      item.type === 'simulation' ? getRunnableSimulationCount(simSubSimulations) : 0;
                    const allSubSimulationsTerminal =
                      item.type === 'simulation' &&
                      simSubSimulations.length > 0 &&
                      simSubSimulations.every((subSim) =>
                        subSim.status ? terminalStatuses.includes(subSim.status) : false
                      );
                    const hasPendingOrRunning =
                      item.type === 'simulation' &&
                      simSubSimulations.some((subSim) => subSim.status && !terminalStatuses.includes(subSim.status));
                    const simulationRunLimitMessage =
                      item.type === 'simulation' && !canBypassUsageLimits
                        ? getSimulationLimitMessage(remainingSimulations, requiredSimulationRuns)
                        : '';
                    const canRunSimulationAction =
                      item.type === 'simulation' &&
                      simGeodataStatus === 'SUCCEEDED' &&
                      item.runId === null &&
                      !runStates[item.id] &&
                      !allSubSimulationsTerminal &&
                      !hasPendingOrRunning &&
                      !simulationRunLimitMessage;
                    const canCloneItem = item.type === 'geodata' || item.type === 'simulation';
                    const canViewYamlAction = item.type === 'geodata' || item.type === 'simulation';
                    const canDeleteItemAction = true;
                    const hasItemActions =
                      canCloneItem || canRunSimulationAction || canViewYamlAction || canDeleteItemAction;

                    let itemIcon = getObjectIcon(item);
                    const itemId = String(item.id);
                    const itemSizeBytes =
                      item.type === 'grid'
                        ? entitySizes.grids[itemId]
                        : item.type === 'geodata'
                          ? entitySizes.geodatas[itemId]
                          : entitySizes.simulations[itemId];
                    const shouldIgnoreRowClick = (event: React.MouseEvent<HTMLElement>) => {
                      const target = event.target as HTMLElement | null;
                      if (!target) return false;
                      return Boolean(target.closest('button, a, input, [role="button"], [data-stop-row-click="true"]'));
                    };

                    return (
                      <React.Fragment key={`second_row_${item.id}`}>
                        <RowComponent
                          key={`row_${item.id}`}
                          item={item}
                          className={`${item.type}-row${isExpandableRow ? ' row-clickable' : ''}`}
                          style={{
                            background:
                              item?.id === lastAddedItem?.id ? 'linear-gradient(to right,#333399,#ff00cc)' : '',
                            cursor: isExpandableRow ? 'pointer' : 'default',
                            color: item.deletedAt ? 'var(--mui-palette-text-disabled, #9e9e9e)' : undefined,
                          }}
                          onClick={(event: React.MouseEvent<HTMLElement>) => {
                            if (!isExpandableRow || openActionsDetail || shouldIgnoreRowClick(event)) {
                              return;
                            }
                            setOpenNodes((prevOpenNodes) =>
                              prevOpenNodes.includes(item.id)
                                ? prevOpenNodes.filter((nodeId) => nodeId !== item.id)
                                : [...prevOpenNodes, item.id]
                            );
                          }}
                        >
                          <CellTreeComponent item={item}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                minWidth: 0,
                              }}
                            >
                              {itemIcon}
                              <span
                                data-stop-row-click="true"
                                style={{ flex: 1, minWidth: 0 }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                                onDoubleClick={(event) => {
                                  event.stopPropagation();
                                }}
                              >
                                <EditableLabelState
                                  item={item}
                                  onSave={(value) => {
                                    if (item.type === 'geodata') {
                                      return updateGeodata(String(item.id), { name: value });
                                    }
                                    if (item.type === 'grid') {
                                      return updateGrid(String(item.id), { name: value });
                                    }
                                    if (item.type === 'simulation') {
                                      return updateSimulationSet(String(item.id), { name: value });
                                    }

                                    throw new Error(
                                      `Unsupported item type for rename: ${String((item as { type?: string }).type)}`
                                    );
                                  }}
                                  useTruncation
                                  maxLength={item.type === 'grid' ? GRID_NAME_MAX_LENGTH : undefined}
                                  onSingleClick={
                                    isExpandableRow
                                      ? () =>
                                          setOpenNodes((prevOpenNodes) =>
                                            prevOpenNodes.includes(item.id)
                                              ? prevOpenNodes.filter((nodeId) => nodeId !== item.id)
                                              : [...prevOpenNodes, item.id]
                                          )
                                      : undefined
                                  }
                                />
                              </span>
                            </div>
                          </CellTreeComponent>
                          <Cell>
                            <div className="center-cell">
                              {item?.type === 'grid' ? (
                                <div
                                  data-stop-row-click="true"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  {createVisualizerLink(item)}
                                </div>
                              ) : null}
                              {item?.type === 'geodata' && (
                                <>
                                  {isGeodataSucceeded && (
                                    <Checkbox
                                      checkedIcon={<CheckedIcon />}
                                      icon={<UnCheckedIcon />} // Custom icon for unchecked state
                                      className="subsimulation-checkbox"
                                      checked={!!allGeodatas[item.id]?.isActive || false} // Convert to boolean
                                      disabled={allGeodatas[item.id]?.status !== 'SUCCEEDED'}
                                      onChange={async (e) => {
                                        const isChecked = e.target.checked;

                                        updateGeodataActive(item.id, isChecked);
                                        context.notification.show(
                                          `${item.name} has been ${isChecked ? 'activated' : 'deactivated'}`,
                                          'success',
                                          true,
                                          'toast'
                                        );
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                      }}
                                    />
                                  )}
                                </>
                              )}

                              {item.type === 'simulation' &&
                                simGeodataStatus === 'SUCCEEDED' &&
                                item.runId === null &&
                                !runStates[item.id] &&
                                !allSubSimulationsTerminal &&
                                !hasPendingOrRunning && (
                                  <Tooltip
                                    title={
                                      isStorageLimitReachedForRuns
                                        ? STORAGE_LIMIT_REACHED_MESSAGE
                                        : simulationRunLimitMessage || 'Run Simulation'
                                    }
                                  >
                                    <span data-stop-row-click="true" style={{ display: 'inline-flex' }}>
                                      <IconButton
                                        color="success"
                                        disabled={!canRunSimulationAction}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          if (!canRunSimulationAction) {
                                            return;
                                          }
                                          void handleRunClick(item);
                                        }}
                                      >
                                        <PlayCircle className="untitled-icons" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                            </div>
                          </Cell>
                          <Cell>
                            <div className="center-cell">
                              {item.type === 'geodata' && (
                                <GeodataStatus
                                  infoObjectStatuses={geodataStatuses}
                                  geodataTasks={geodataTaskStatuses}
                                  geodata={item}
                                  projectId={currentProject.id}
                                />
                              )}
                            </div>
                          </Cell>
                          <Cell>
                            <div className="center-cell">{showSizes ? formatBytes(itemSizeBytes) : ''}</div>
                          </Cell>
                          <Cell>
                            <div className="center-cell">
                              {item.type === 'grid' && (
                                <span data-stop-row-click="true">
                                  <InfoIcon
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      navigate(`/grids/${item.id}/review`);
                                    }}
                                  />
                                </span>
                              )}
                              {item.type === 'geodata' && (
                                <span data-stop-row-click="true">
                                  <InfoIcon
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      navigate(`/geodatas/${item.id}/review`);
                                    }}
                                  />
                                </span>
                              )}
                              {item.type === 'simulation' && (
                                <span data-stop-row-click="true">
                                  <InfoIcon
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      navigate(`/simulations/${item.id}/review`);
                                    }}
                                  />
                                </span>
                              )}
                              <span
                                data-stop-row-click="true"
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              ></span>
                            </div>
                          </Cell>

                          <Cell>
                            <div className="center-cell">
                              <>
                                <span
                                  data-stop-row-click="true"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                  }}
                                >
                                  <IconButton
                                    disabled={!hasItemActions}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleClickActionsDetail(event, item);
                                    }}
                                  >
                                    <VerticalDotsIcon />
                                  </IconButton>
                                </span>
                                <Menu
                                  anchorEl={anchorElActionsDetail}
                                  open={openActionsDetail && selectedItem?.id === item.id}
                                  onClose={handleMenuCloseActionsDetail}
                                  className="actions-menu"
                                >
                                  {selectedItem?.type === 'grid' && (
                                    <Tooltip title={gridCloneTooltip}>
                                      <span>
                                        <MenuItem
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            void handleClone(selectedItem);
                                          }}
                                          className="actions-menu-item"
                                          disabled={isGridCloneDisabled}
                                        >
                                          <IconButton disabled={isGridCloneDisabled}>
                                            {isGridCloneInProgress ? (
                                              <CircularProgress size={20} color="inherit" />
                                            ) : (
                                              <CopyIcon />
                                            )}
                                          </IconButton>
                                        </MenuItem>
                                      </span>
                                    </Tooltip>
                                  )}

                                  {selectedItem?.type === 'geodata' && (
                                    <Tooltip title={geodataCloneTooltip}>
                                      <span>
                                        <MenuItem
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            void handleGeodataClone(selectedItem);
                                          }}
                                          className="actions-menu-item"
                                          disabled={isGeodataCloneDisabled}
                                        >
                                          <IconButton disabled={isGeodataCloneDisabled}>
                                            {isGeodataCloneInProgress ? (
                                              <CircularProgress size={20} color="inherit" />
                                            ) : (
                                              <CopyIcon />
                                            )}
                                          </IconButton>
                                        </MenuItem>
                                      </span>
                                    </Tooltip>
                                  )}

                                  {selectedItem?.type === 'simulation' && (
                                    <Tooltip title={simulationCloneTooltip}>
                                      <span>
                                        <MenuItem
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleSimulationClone(selectedItem);
                                          }}
                                          className="actions-menu-item"
                                          disabled={isSimulationCloneDisabled}
                                        >
                                          <IconButton disabled={isSimulationCloneDisabled}>
                                            {isSimulationCloneInProgress ? (
                                              <CircularProgress size={20} color="inherit" />
                                            ) : (
                                              <CopyIcon />
                                            )}
                                          </IconButton>
                                        </MenuItem>
                                      </span>
                                    </Tooltip>
                                  )}

                                  {selectedItem?.type === 'simulation' &&
                                    (() => {
                                      const selectedSimulationSubSimulations =
                                        allSubSimulations[selectedItem.id] ?? selectedItem.subSimulations ?? [];
                                      const selectedSimulationGeodataStatus =
                                        geodataStatuses[selectedItem.geodataId] ??
                                        allGeodatas[selectedItem.geodataId]?.status ??
                                        '';
                                      const terminalStatuses = ['SUCCEEDED', 'FAILED', 'ABORTED', 'TIMED_OUT'];
                                      const selectedAllSubSimulationsTerminal =
                                        selectedSimulationSubSimulations.length > 0 &&
                                        selectedSimulationSubSimulations.every((subSim) =>
                                          subSim.status ? terminalStatuses.includes(subSim.status) : false
                                        );
                                      const selectedHasPendingOrRunning = selectedSimulationSubSimulations.some(
                                        (subSim) => subSim.status && !terminalStatuses.includes(subSim.status)
                                      );
                                      const selectedRequiredSimulationRuns = getRunnableSimulationCount(
                                        selectedSimulationSubSimulations
                                      );
                                      const selectedSimulationLimitMessage = canBypassUsageLimits
                                        ? ''
                                        : getSimulationLimitMessage(
                                            remainingSimulations,
                                            selectedRequiredSimulationRuns
                                          );
                                      const canShowSelectedSimulationAction =
                                        selectedSimulationGeodataStatus === 'SUCCEEDED' &&
                                        selectedItem.runId === null &&
                                        !runStates[selectedItem.id] &&
                                        !selectedAllSubSimulationsTerminal &&
                                        !selectedHasPendingOrRunning;

                                      if (!canShowSelectedSimulationAction) {
                                        return null;
                                      }

                                      return (
                                        <MenuItem
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleCloseActionsDetail();
                                            if (isStorageLimitReachedForRuns) {
                                              context.notification.show(
                                                STORAGE_LIMIT_REACHED_MESSAGE,
                                                'error',
                                                true,
                                                'toast'
                                              );
                                              return;
                                            }
                                            if (selectedSimulationLimitMessage) {
                                              context.notification.show(
                                                selectedSimulationLimitMessage,
                                                'error',
                                                true,
                                                'toast'
                                              );
                                              return;
                                            }
                                            void handleRunClick(selectedItem);
                                          }}
                                          className="actions-menu-item"
                                        >
                                          <Tooltip
                                            title={
                                              isStorageLimitReachedForRuns
                                                ? STORAGE_LIMIT_REACHED_MESSAGE
                                                : selectedSimulationLimitMessage || 'Run Simulation'
                                            }
                                          >
                                            <IconButton>
                                              <PlayCircle className="untitled-icons" />
                                            </IconButton>
                                          </Tooltip>
                                        </MenuItem>
                                      );
                                    })()}

                                  {canViewYamlAction &&
                                    (selectedItem?.type === 'geodata' || selectedItem?.type === 'simulation') && (
                                      <MenuItem
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          if (selectedItem?.type === 'geodata') {
                                            handleOpenGeodataYamlMenu(event);
                                            return;
                                          }

                                          handleDownloadYaml(selectedItem);
                                        }}
                                        className="actions-menu-item"
                                      >
                                        <Tooltip
                                          title={`Check ${selectedItem?.type === 'geodata' ? 'Geodata' : 'Simulation Set'} YAML`}
                                        >
                                          <IconButton>
                                            <DescriptionIcon onClick={undefined} />
                                          </IconButton>
                                        </Tooltip>
                                      </MenuItem>
                                    )}

                                  {canDeleteItemAction && (
                                    <MenuItem
                                      disabled={isResourceDeleteDisabled}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleDeleteItem(selectedItem);
                                      }}
                                      className="actions-menu-item"
                                    >
                                      <Tooltip title={`Delete ${selectedItem?.name}`}>
                                        <IconButton
                                          aria-label="delete"
                                          color="error"
                                          disabled={isResourceDeleteDisabled}
                                          onClick={handleCloseActionsDetail}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </MenuItem>
                                  )}
                                </Menu>
                                <Menu
                                  anchorEl={anchorElGeodataYaml}
                                  open={openGeodataYamlMenu && selectedItem?.id === item.id}
                                  onClose={handleCloseGeodataYamlMenu}
                                  className="actions-menu"
                                >
                                  {selectedItem?.type === 'geodata' &&
                                    GEODATA_YAML_OPTIONS.map((option) => (
                                      <MenuItem
                                        key={option.kind}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleDownloadYaml(selectedItem, option.kind);
                                        }}
                                        className="actions-menu-item"
                                      >
                                        <Typography>{option.label}</Typography>
                                      </MenuItem>
                                    ))}
                                </Menu>
                              </>
                            </div>
                          </Cell>
                          <Cell>
                            <div className="center-cell">
                              {item.type === 'grid' && item.terrainType !== 'customScene' && (
                                <Tooltip title={geodataButtonTooltip}>
                                  <span data-stop-row-click="true" style={{ display: 'inline-flex' }}>
                                    <UMButton
                                      text={isGridDraft ? 'Complete Grid' : 'Add Geodata'}
                                      icon={
                                        !isGridDraft && hasNoGeodatasAvailable
                                          ? (props: { size?: number | string; className?: string }) => (
                                              <RemainingCountAlertIcon {...props} pluralLabel="geodata modules" />
                                            )
                                          : undefined
                                      }
                                      onClick={(event: any) => {
                                        event.stopPropagation();
                                        if (isGridDraft) {
                                          handleResumeDraftEdit(
                                            item.geodatas.find((geodata) => Boolean(getDraftResumeTarget(geodata))) ??
                                              null
                                          );
                                        } else {
                                          handleClickAdd(item, hasNoGeodatasAvailable);
                                        }
                                      }}
                                      disabled={
                                        isGridDraft
                                          ? !item.geodatas.some((geodata) => Boolean(getDraftResumeTarget(geodata)))
                                          : geodataButtonDisabled
                                      }
                                      style={{
                                        minWidth: !isGridDraft && hasNoGeodatasAvailable ? '156px' : '132px',
                                        paddingInline: '14px',
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {item.type === 'geodata' && !isParentGridDraft && (
                                <Tooltip title={simulationButtonTooltip}>
                                  <span data-stop-row-click="true" style={{ display: 'inline-flex' }}>
                                    <UMButton
                                      text="Add Simulation"
                                      icon={
                                        hasNoSimulationsAvailable
                                          ? (props: { size?: number | string; className?: string }) => (
                                              <RemainingCountAlertIcon {...props} pluralLabel="simulations" />
                                            )
                                          : undefined
                                      }
                                      onClick={(event: any) => {
                                        event.stopPropagation();
                                        handleClickAdd(item, hasNoSimulationsAvailable);
                                      }}
                                      disabled={simulationButtonDisabled}
                                      style={{
                                        minWidth: hasNoSimulationsAvailable ? '172px' : '142px',
                                        paddingInline: '14px',
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </Cell>
                        </RowComponent>
                        {item.type === 'simulation' && isOpen && (
                          <SubSimulation
                            simulation={item}
                            subSimulationSizes={entitySizes.subSimulations}
                            isDeleteDisabled={isResourceDeleteDisabled}
                            showDeletedItems={showDeletedItems}
                            showSizes={showSizes}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Body>
              </React.Fragment>
            )}
          </Table>
        )}

        {!isRunDataLoading && currentProject && (
          <Reports currentProject={currentProject} showDeletedItems={showDeletedItems} />
        )}

        <div
          id="project-files"
          ref={filesSectionRef}
          style={{
            scrollMarginTop: 20,
            borderRadius: 12,
            transition: 'background-color 300ms ease, box-shadow 300ms ease',
            backgroundColor: isFilesSectionHighlighted ? 'rgba(47, 128, 237, 0.1)' : 'transparent',
            boxShadow: isFilesSectionHighlighted ? '0 0 0 4px rgba(47, 128, 237, 0.18)' : 'none',
          }}
        >
          <ImportedFile projectId={projectId ?? null} showDeletedItems={showDeletedItems} />
          <ExportedFiles
            projectId={projectId ?? null}
            showDeletedItems={showDeletedItems}
            grids={currentProject?.grids ?? []}
          />
        </div>
        <VolumeUsageDialog
          open={volumeDialogGridId !== null}
          gridId={volumeDialogGridId}
          onClose={() => {
            setVolumeDialogGridId(null);
            setVolumeDialogRunItem(null);
          }}
          onConsumed={(usage: GridVolumeUsage) => {
            if (currentProject) setCurrentProject({ ...currentProject, paidVolume: usage.paidVolume });
            if (volumeDialogRunItem) runSimulation(volumeDialogRunItem);
          }}
        />
      </div>
    </div>
  );
};

export default Project;
